import React, { useRef } from 'react';
import { PrismicRichText } from '@prismicio/react';
import styled from 'styled-components';
import { BREAKPOINT, CONTENT_MAX_WIDTH_PX } from '@constants';
import { PageSlice, SliceFlexibleGridDetails, SliceFlexibleGridItem } from '@contracts';
import Colors from '../Colors';
import useDesktop from '../useDesktop';
import CustomCarousel from './CustomCarousel';
import FlexibleGridItem from './FlexibleGridItem';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';

interface GridProps {
  slice: PageSlice;
}

const FlexibleGrid: React.FC<GridProps> = ({ slice }) => {
  const sliceDetails = slice.primary as SliceFlexibleGridDetails;
  const sliceItems = slice.items as SliceFlexibleGridItem[];
  const wrapperRef = useRef<HTMLElement>(null);
  const isDesktop = useDesktop(BREAKPOINT.tablet);

  return (
    <Wrapper $background={sliceDetails?.background_text} ref={wrapperRef}>
      <HeadingWrapper $alignmentMobile={sliceDetails?.heading_alignment_mobile}>
        {sliceDetails?.overline && <Typography $variant={TextVariant.UpperText4}>{sliceDetails.overline}</Typography>}
        <Heading>
          <PrismicRichText field={sliceDetails.heading.richText} />
        </Heading>
      </HeadingWrapper>
      {isDesktop ? (
        <GridWrap>
          {sliceItems.map((item, i) => (
            <GridItemWrap key={i} $width={item.width}>
              <FlexibleGridItem item={item} index={i} includeOverlay={sliceDetails.include_overlay} />
            </GridItemWrap>
          ))}
        </GridWrap>
      ) : (
        <CarouselWrap>
          <CustomCarousel height="320px">
            {sliceItems.map((item, i) => (
              <FlexibleGridItem key={i} item={item} index={i} includeOverlay={sliceDetails.include_overlay} />
            ))}
          </CustomCarousel>
        </CarouselWrap>
      )}
    </Wrapper>
  );
};

export default FlexibleGrid;

const Wrapper = styled.section<{ $background?: string }>`
  background-color: ${(props) => props.$background || Colors.cream};
  width: 100%;

  padding: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 32px;
  overflow-x: hidden;

  > * {
    max-width: ${CONTENT_MAX_WIDTH_PX};
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    padding: 56px 20px;
  }
`;

const HeadingWrapper = styled.div<{ $alignmentMobile?: string }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  max-width: ${CONTENT_MAX_WIDTH_PX};

  @media (max-width: ${BREAKPOINT.tablet}px) {
    display: block;
    align-self: ${(props) => props.$alignmentMobile || 'center'};
  }
`;

const Heading = styled.div`
  > * {
    color: ${Colors.navyBlue};
    ${TEXT_STYLES[TextVariant.Heading4]};

    @media (max-width: ${BREAKPOINT.tablet}px) {
      ${TEXT_STYLES[TextVariant.Heading5]};
    }
  }
`;

const GridWrap = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  max-width: ${CONTENT_MAX_WIDTH_PX};

  @media (max-width: ${BREAKPOINT.tablet}px) {
    flex-direction: column;
  }
`;

const GridItemWrap = styled.div<{ $width: string }>`
  flex: ${(props) => (props.$width ? `0 1 ${props.$width}%` : '0 1 50%')};
  padding: 8px;

  @media (max-width: ${BREAKPOINT.tablet}px) {
    flex: 0 0 100%;
  }
`;

const CarouselWrap = styled.div`
  width: 100%;
  overflow: visible;
`;
